import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import cn from 'clsx';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import CopyButton from '@components/core/copy-button';
import { checkStep4Availability } from './step4-overview/step4-overview.helper';
import { useTranslation } from 'hooks';
import { PanelColumnType, PanelGraphType, PanelType } from 'types/graphql-global-types';
import { checkConfigIdErrors, checkIfNameExist, checkRequiredFields, validateFields } from 'utils/validation';
import PanelConstants from 'views/components/central-monitoring/panels/add-panel/panel-constants';
import PanelService from 'views/components/central-monitoring/panels/add-panel/service';
import CharactersRemaining from 'views/components/core/characters-remaining';
import OutlinedWrapper from 'views/components/core/outlined-wrapper';
import SelectWithCategories from 'views/components/core/select-with-categories';
import { SelectWithSearchDataArrayInterface } from 'views/components/core/select-with-categories/select-with-categories.interface';
import TextField from 'views/components/core/text-field';

import 'views/components/central-monitoring/panels/add-panel/styles.scss';

const typeOptions = [
    PanelType.MODEL,
    PanelGraphType.HEATMAP,
    PanelGraphType.COLUMN,
    PanelGraphType.SCATTER_PLOT_LINE,
    PanelGraphType.DONUT,
    PanelGraphType.STACKED_COLUMN,
];

const modelOptions = Object.values(PanelConstants.modelOptionsDropdown);

const modelsWithExtraMenu = [
    PanelConstants.modelOptions.NEGATIVE_BINOMIAL,
    PanelConstants.modelOptions.FUNNEL,
    PanelConstants.modelOptions.RATE_GROUP,
    PanelConstants.modelOptions.TRI_OPERATIONAL,
];

const columnTypeOptions = [PanelColumnType.STANDARD, PanelColumnType.RANGED];

const Step1 = forwardRef(
    (
        {
            panelState,
            setPanelState,
            setPage,
            show,
            clean2Step,
            initialPanelState,
            updateState,
            page,
            updateErrorState,
            panels,
            panelId,
            canSave,
            isSysAdmin,
        }: any,
        ref,
    ) => {
        const { t } = useTranslation();
        const minWeight = 1;
        const initialState = {
            id: panelState.id,
            configId: panelState.configId,
            active: panelState.active as boolean,
            name: panelState.name,
            plotTitle: panelState.graph.plotTitle,
            xTitle: panelState.graph.xTitle,
            yTitle: panelState.graph.yTitle,
            plotThreshold: panelState.graph.plotThreshold,
            panelType: PanelService.getPanelType(panelState),
            panelModel: PanelService.getPanelModel(panelState.modelParams),
            columnType: panelState.graph.columnType ?? '',
            weight: panelState.panelType === PanelType.MODEL ? panelState.weight : minWeight,
            includeInSiteScoring: panelState.includeInSiteScoring,
            decision: panelState.decision,
        };
        const initialErrors = {
            weight: '',
            name: '',
            plotTitle: '',
            xTitle: '',
            panelType: '',
            panelModel: '',
            plotThreshold: '',
            columnType: '',
            configId: '',
        } as any;

        const [state, setState] = useState({ ...initialState });
        const [errors, setErrors] = useState({ ...initialErrors });

        useEffect(() => {
            if (panelState)
                setState({
                    id: panelState.id,
                    configId: panelState.configId,
                    active: panelState.active as boolean,
                    name: panelState.name,
                    plotTitle: panelState.graph.plotTitle,
                    xTitle: panelState.graph.xTitle,
                    yTitle: panelState.graph.yTitle,
                    panelType: PanelService.getPanelType(panelState),
                    panelModel: PanelService.getPanelModel(panelState.modelParams),
                    plotThreshold: panelState.graph.plotThreshold,
                    weight: panelState.panelType === PanelType.MODEL ? panelState.weight : minWeight,
                    includeInSiteScoring: panelState.includeInSiteScoring,
                    decision: panelState.decision,
                    columnType: panelState.graph.columnType,
                });
        }, [panelState]);

        useEffect(() => {
            updateErrorState(errors, 1);
        }, [errors]);

        useEffect(() => {
            if (panelState.panelType === PanelType.GRAPH && errors.weight !== '') {
                setErrors({ ...errors, weight: '' });
            }
        }, [errors]);

        const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
            const error = validateFields(value, field, length, required);
            setErrors({
                ...errors,
                ...error,
            });
        };

        const numberValidation = (
            value: number,
            fieldName: string,
            min: number,
            max: number,
            errorText: string,
            equal: boolean,
        ) => {
            let error = { [fieldName]: '' };
            if ((equal && (value >= max || value <= min)) || value > max || value < min)
                error = { [fieldName]: errorText };
            setErrors({
                ...errors,
                ...error,
            });
        };

        const onChangeNumber =
            (fieldName: string, errorText?: string, min?: number, max?: number, key?: string, equal?: boolean) =>
            (event: any) => {
                const { value } = event.target;
                updateState({ target: { name: key || fieldName, value } });

                setState({ ...state, [fieldName]: value });
                if (min !== undefined || max !== undefined)
                    numberValidation(+value, fieldName, min, max, errorText, equal);
            };

        const onChange = (fieldName: string, length?: number, required?: boolean, key?: string) => (event: any) => {
            const { value } = event.target;
            if (fieldName === 'columnType') {
                setState({ ...state, [fieldName]: value });
                updateState({ target: { name: key, value } });
                validate(value, fieldName, length, required);
                if (page.available > 1) clean2Step(true, PanelService.setModelParamsType(state.panelModel));
            } else if (fieldName === 'panelModel') {
                setState({ ...state, [fieldName]: value });
                updateState({ target: { name: key || fieldName, value } });
                validate(value, fieldName, length, required);
                if (
                    (value === PanelConstants.modelOptions.RATE_GROUP &&
                        state.panelModel === PanelConstants.modelOptions.NEGATIVE_BINOMIAL) ||
                    (value === PanelConstants.modelOptions.NEGATIVE_BINOMIAL &&
                        state.panelModel === PanelConstants.modelOptions.RATE_GROUP)
                )
                    return;
                if (page.available > 1) clean2Step(undefined, value);
            } else if (fieldName === 'panelType') {
                setState({
                    ...state,
                    panelType: value,
                    panelModel: null,
                });
                setPanelState({
                    id: state.id,
                    configId: state.configId,
                    active: state.active,
                    name: state.name,
                    graph: {
                        ...initialPanelState.graph,
                        plotTitle: state.plotTitle,
                        plotThreshold: state.plotThreshold,
                        xTitle: state.xTitle,
                        yTitle: state.yTitle,
                        type: PanelService.setPanelType(value, state.panelType, panelState),
                        columnType: state.columnType,
                    },
                    weight: state.panelType === PanelType.MODEL ? state.weight : minWeight,
                    includeInSiteScoring: state.includeInSiteScoring,
                    panelType: PanelService.setGraphType(value, state.panelType),
                    decision: state.decision,
                    modelParams: {
                        ...initialPanelState.modelParams,
                        type: null,
                    },
                });

                validate(value, fieldName, length, required);
                if (page.available > 1) clean2Step(true);
            } else {
                if (fieldName === 'weight' && !/^[0-9]*$/.test(value)) return;

                setState({ ...state, [fieldName]: value });
                updateState({ target: { name: key || fieldName, value } });
                validate(value, fieldName, length, required);
            }
        };

        const onCheckboxChange = (fieldName: any) => () => {
            updateState({ target: { name: fieldName, type: 'checkbox', value: !(state as any)[fieldName] } });
            setState({ ...state, [fieldName]: !(state as any)[fieldName] });
        };

        const getPanelTypeOptions = (): SelectWithSearchDataArrayInterface[] => {
            const panelTypeOptions = typeOptions.map((value: any) => ({
                default: false,
                active: true,
                id: value,
                text: t(`admin:${value}`),
            }));
            panelTypeOptions.unshift({ default: true, active: true, id: '', text: t('admin:selectPanelType') });
            return panelTypeOptions;
        };

        const panelTypeOptions = useMemo(getPanelTypeOptions, []);

        const getModelOptions = (): SelectWithSearchDataArrayInterface[] => {
            const modalOptions = modelOptions.map((value) => ({
                default: false,
                active: true,
                id: value,
                text: t(`admin:${value}`),
            }));
            modalOptions.unshift({ default: true, active: true, id: null, text: t('admin:selectPanelModel') });
            return modalOptions;
        };

        const modelSelectOptions = useMemo(getModelOptions, []);

        const getPanelColumnTypeOptions = (): SelectWithSearchDataArrayInterface[] => {
            const panelColumnTypeOptions = columnTypeOptions.map((value: any) => ({
                default: false,
                active: true,
                id: value,
                text: t(`admin:${value}`),
            }));
            panelColumnTypeOptions.unshift({ default: true, active: true, id: '', text: t('admin:selectColumnType') });
            return panelColumnTypeOptions;
        };

        const panelColumnTypeOptions = useMemo(getPanelColumnTypeOptions, []);

        const validationOnUpdate = () => {
            const requiredFields = ['name', 'plotTitle', 'xTitle', 'yTitle', 'panelType', 'plotThreshold'];

            if (panelId) {
                requiredFields.push('configId');
                const configIdError = checkConfigIdErrors(state, panels);

                if (configIdError) {
                    setErrors({
                        ...errors,
                        configId: configIdError,
                    });
                    return;
                }
            }

            if (state.panelType === PanelType.MODEL) requiredFields.push('panelModel');
            else if (state.panelType === PanelGraphType.COLUMN) requiredFields.push('columnType');
            if (modelsWithExtraMenu.includes(state.panelModel)) {
                requiredFields.push('weight');
                if ((state.weight > 10 || state.weight < 1) && state.weight.length) {
                    setErrors({
                        ...errors,
                        weight: t('admin:numericValueMinMax', { min: 1, max: 10 }),
                    });
                    return;
                }
            }

            if (checkRequiredFields({ fields: requiredFields, state, setErrors, errors })) {
                const errorName = checkIfNameExist({ id: panelId, ...state }, panels, 'name')
                    ? t('admin:panelAlreadyExist')
                    : null;
                setErrors({
                    ...errors,
                    name: errorName,
                });
                return !errorName;
            }
            return false;
        };

        const onFirstStepSave = (withoutNav?: boolean) => {
            if (validationOnUpdate()) {
                const stateToSet = page.available > 1 ? panelState : initialPanelState;
                setPanelState({
                    ...stateToSet,
                    configId: state.configId,
                    active: state.active,
                    name: state.name,
                    showStep4: checkStep4Availability({
                        modelParams: { type: state.panelModel },
                        panelType: state.panelType,
                    }),
                    graph: {
                        ...stateToSet.graph,
                        plotTitle: state.plotTitle,
                        xTitle: state.xTitle,
                        yTitle: state.yTitle,
                        plotThreshold: state.plotThreshold,
                        type: PanelService.setPanelType(null, state.panelType, panelState),
                        columnType: state.columnType,
                    },
                    weight: state.panelType === PanelType.MODEL ? state.weight : minWeight,
                    includeInSiteScoring: state.includeInSiteScoring,
                    panelType: PanelService.setGraphType(null, state.panelType),
                    decision: state.decision,
                    modelParams: {
                        ...stateToSet.modelParams,
                        type: PanelService.setModelParamsType(state.panelModel),
                        rateGroupType: PanelService.setRateGroupType(state.panelModel),
                    },
                });
                if (!withoutNav) {
                    if (state.panelType === PanelGraphType.SCATTER_PLOT_LINE)
                        setPage({ number: 2, params: 'line', available: page.available < 2 ? 2 : page.available });
                    if (state.panelType === PanelType.MODEL && state.panelModel === PanelConstants.modelOptions.FUNNEL)
                        setPage({
                            number: 2,
                            params: 'funnelModel',
                            available: page.available < 2 ? 2 : page.available,
                        });
                    if (state.panelType === PanelGraphType.HEATMAP)
                        setPage({ number: 2, params: 'heatMap', available: page.available < 2 ? 2 : page.available });
                    if (state.panelType === PanelGraphType.COLUMN)
                        setPage({ number: 2, params: 'column', available: page.available < 2 ? 2 : page.available });
                    if (
                        state.panelType === PanelType.MODEL &&
                        state.panelModel === PanelConstants.modelOptions.FUNNEL_TREND
                    )
                        setPage({
                            number: 2,
                            params: 'funnelTrendModel',
                            available: page.available < 2 ? 2 : page.available,
                        });
                    if (
                        state.panelType === PanelType.MODEL &&
                        state.panelModel === PanelConstants.modelOptions.NEGATIVE_BINOMIAL
                    )
                        setPage({
                            number: 2,
                            params: 'negativeBinomial',
                            available: page.available < 2 ? 2 : page.available,
                        });
                    if (
                        state.panelType === PanelType.MODEL &&
                        state.panelModel === PanelConstants.modelOptions.RATE_GROUP
                    )
                        setPage({
                            number: 2,
                            params: 'poissonModel',
                            available: page.available < 2 ? 2 : page.available,
                        });
                    if (
                        state.panelType === PanelType.MODEL &&
                        state.panelModel === PanelConstants.modelOptions.RATE_GROUP_TREND
                    )
                        setPage({
                            number: 2,
                            params: 'poissonTrend',
                            available: page.available < 2 ? 2 : page.available,
                        });
                    if (
                        state.panelType === PanelType.MODEL &&
                        state.panelModel === PanelConstants.modelOptions.TRI_OPERATIONAL
                    )
                        setPage({
                            number: 2,
                            params: 'triOperational',
                            available: page.available < 2 ? 2 : page.available,
                        });
                    if (
                        state.panelType === PanelType.MODEL &&
                        state.panelModel === PanelConstants.modelOptions.TRI_OPERATIONAL_TREND
                    )
                        setPage({
                            number: 2,
                            params: 'triOperationalTrendModel',
                            available: page.available < 2 ? 2 : page.available,
                        });
                    if (state.panelType === PanelGraphType.DONUT)
                        setPage({ number: 2, params: 'donut', available: page.available < 2 ? 2 : page.available });
                    if (state.panelType === PanelGraphType.STACKED_COLUMN)
                        setPage({
                            number: 2,
                            params: 'stackedColumn',
                            available: page.available < 2 ? 2 : page.available,
                        });
                }
                return true;
            }
        };

        useImperativeHandle(ref, () => ({
            nextButton(withoutNav?: boolean) {
                return onFirstStepSave(withoutNav);
            },
            validationOnUpdate() {
                return validationOnUpdate();
            },
        }));

        return (
            <div style={!show ? { display: 'none' } : {}}>
                <div className="checkboxes-wrapper">
                    <div className="left-part">
                        <Checkbox
                            rounded={null}
                            id="add-panel-active"
                            className="cell-checkbox no-border"
                            checked={state.active}
                            onChange={onCheckboxChange('active')}
                            label={t('active')}
                            disabled={!canSave || isSysAdmin}
                            labelClassName={cn({ 'checkbox-label-disabled': !canSave })}
                            data-testid="add-panel-active"
                            size="medium"
                        />
                    </div>
                </div>
                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className={cn('form-control-type', { 'not-full mr': panelId })}>
                        <TextField
                            label={t('admin:simplifiedPlotThreshold')}
                            id="add-panel-plotThreshold"
                            fullWidth
                            value={state.plotThreshold}
                            onChange={onChangeNumber(
                                'plotThreshold',
                                t('admin:numericValueMinMax', { min: '0', max: '100000' }),
                                0,
                                100000,
                                'graph.plotThreshold',
                            )}
                            placeholder={t('admin:enterSimplifiedPlotThreshold')}
                            type="number"
                            helperText={
                                <span
                                    style={errors.plotThreshold ? { color: '#ae0015' } : {}}
                                    className="remaining-text"
                                >
                                    {errors.plotThreshold
                                        ? errors.plotThreshold
                                        : t('admin:numericValueMinMax', { min: '0', max: '100000' })}
                                </span>
                            }
                            error={!!errors.plotThreshold}
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                            disabled={!canSave || isSysAdmin}
                        />
                    </FormControl>
                    {!!panelId && (
                        <FormControl variant="outlined" className="form-control-type not-full">
                            <TextField
                                id="edit-field-config-id"
                                label={t('admin:configId')}
                                onChange={onChange('configId', 24)}
                                fullWidth
                                value={state.configId}
                                slotProps={{
                                    input: {
                                        readOnly: !isSysAdmin,
                                        endAdornment: <CopyButton value={state.configId ?? ''} />,
                                    },
                                }}
                                helperText={
                                    <span style={{ color: '#ae0015' }} className="remaining-text">
                                        {errors.configId}
                                    </span>
                                }
                                variant="outlined"
                                color="secondary"
                                error={!!errors.configId}
                            />
                        </FormControl>
                    )}
                </div>

                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type not-full mr">
                        <TextField
                            label={t('admin:panelName')}
                            id="add-panel-name"
                            fullWidth
                            value={state.name}
                            onChange={onChange('name', 30)}
                            placeholder={t('admin:enterPanelName')}
                            helperText={
                                <CharactersRemaining
                                    id="add-panel-name-help-text"
                                    length={state.name?.length}
                                    max={30}
                                    error={errors.name}
                                />
                            }
                            error={!!errors.name}
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                            disabled={!canSave || isSysAdmin}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type not-full">
                        <TextField
                            label={t('admin:plotTitle')}
                            id="add-panel-plotTitle"
                            fullWidth
                            value={state.plotTitle}
                            onChange={onChange('plotTitle', 50, false, 'graph.plotTitle')}
                            placeholder={t('admin:enterPlotTitle')}
                            helperText={
                                <CharactersRemaining
                                    id="add-panel-plotTitle-help-text"
                                    length={state.plotTitle?.length}
                                    max={50}
                                    error={errors.plotTitle}
                                />
                            }
                            error={!!errors.plotTitle}
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                            disabled={!canSave || isSysAdmin}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type not-full mr">
                        <TextField
                            label={t('admin:xAxisTitle')}
                            id="add-panel-xTitle"
                            fullWidth
                            value={state.xTitle}
                            onChange={onChange('xTitle', 50, false, 'graph.xTitle')}
                            placeholder={t('admin:enterXAxisTitle')}
                            helperText={
                                <CharactersRemaining
                                    id="add-panel-xTitle-help-text"
                                    length={state.xTitle?.length}
                                    max={50}
                                    error={errors.xTitle}
                                />
                            }
                            error={!!errors.xTitle}
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                            disabled={!canSave || isSysAdmin}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type not-full">
                        <TextField
                            label={t('admin:yAxisTitle')}
                            id="add-panel-yTitle"
                            fullWidth
                            value={state.yTitle}
                            onChange={onChange('yTitle', 50, false, 'graph.yTitle')}
                            placeholder={t('admin:enterYAxisTitle')}
                            helperText={
                                <CharactersRemaining
                                    id="add-panel-yTitle-help-text"
                                    length={state.yTitle?.length}
                                    max={50}
                                    error={errors.yTitle}
                                />
                            }
                            error={!!errors.yTitle}
                            variant="outlined"
                            color="secondary"
                            className="before-select"
                            disabled={!canSave || isSysAdmin}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type not-full mr">
                        <SelectWithCategories
                            label={t('admin:panelType')}
                            dataArray={panelTypeOptions}
                            onChange={(fieldName?: string) => (event: any) => {
                                onChange(fieldName, null, false, 'graph.type')(event);
                            }}
                            fieldSelect="panelType"
                            currentState={state.panelType}
                            labelField="panelType"
                            showWarning={false}
                            filterable={false}
                            customLabelId="add-panel-panelType"
                            customId="add-panel-panelType"
                            customClassName="add-panel-panelType"
                            error={errors.panelType}
                            disabled={!canSave || isSysAdmin}
                        />
                    </FormControl>
                    {state.panelType === PanelType.MODEL && (
                        <FormControl variant="outlined" className="form-control-type not-full">
                            <SelectWithCategories
                                customClassName="add-panel-panelModel"
                                showWarning={false}
                                fieldSelect="panelModel"
                                onChange={(fieldName?: string) => (event: any) => {
                                    onChange(fieldName, null, false, 'modelParams.type')(event);
                                }}
                                label={t('admin:modelR')}
                                filterable={false}
                                labelField="panelModel"
                                dataArray={modelSelectOptions}
                                customId="add-panel-panelModel"
                                currentState={state.panelModel}
                                error={errors.panelModel}
                                disabled={!canSave || isSysAdmin}
                            />
                        </FormControl>
                    )}
                    {state.panelType === PanelGraphType.COLUMN && (
                        <FormControl variant="outlined" className="form-control-type not-full">
                            <SelectWithCategories
                                customClassName="add-panel-columnType"
                                showWarning={false}
                                fieldSelect="columnType"
                                onChange={(fieldName?: string) => (event: any) => {
                                    onChange(fieldName, null, false, 'graph.columnType')(event);
                                }}
                                label={`${t('admin:type')}*`}
                                filterable={false}
                                labelField="columnType"
                                dataArray={panelColumnTypeOptions}
                                customId="add-panel-columnType"
                                currentState={state.columnType}
                                error={errors.columnType}
                                disabled={!canSave || isSysAdmin}
                            />
                        </FormControl>
                    )}
                </div>
                {modelsWithExtraMenu.includes(state.panelModel) && state.panelType === PanelType.MODEL && (
                    <OutlinedWrapper customClass="mb" label={t('admin:model')}>
                        <div className="type-edc-wrapper no-mb">
                            <FormControl variant="outlined" className="form-control-type not-full mr">
                                <TextField
                                    label={t('admin:weight')}
                                    id="add-panel-weight"
                                    fullWidth
                                    value={state.weight}
                                    onChange={onChange('weight')}
                                    placeholder={t('admin:enterWeight')}
                                    helperText={
                                        <CharactersRemaining
                                            id="model-weight-help-text"
                                            error={errors.weight}
                                            customText={t('admin:numericValueMinMax', { min: 1, max: 10 })}
                                        />
                                    }
                                    error={!!errors.weight}
                                    variant="outlined"
                                    color="secondary"
                                    className="before-select"
                                    disabled={!canSave || isSysAdmin}
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type not-full">
                                <div className="horizontal-checkboxes-wrapper">
                                    <Checkbox
                                        rounded={null}
                                        id="add-panel-includeInScoring"
                                        className="cell-checkbox no-border top"
                                        checked={state.includeInSiteScoring}
                                        onChange={onCheckboxChange('includeInSiteScoring')}
                                        label={t('admin:includeInScoring')}
                                        disabled={!canSave || isSysAdmin}
                                        labelClassName={cn({ 'checkbox-label-disabled': !canSave })}
                                        data-testid="add-panel-includeInScoring"
                                        size="medium"
                                    />
                                    <Checkbox
                                        rounded={null}
                                        id="add-panel-decision"
                                        className="cell-checkbox no-border bottom"
                                        checked={state.decision}
                                        onChange={onCheckboxChange('decision')}
                                        label={t('admin:decision')}
                                        disabled={!canSave || isSysAdmin}
                                        labelClassName={cn({ 'checkbox-label-disabled': !canSave })}
                                        data-testid="add-panel-decision"
                                        size="medium"
                                    />
                                </div>
                            </FormControl>
                        </div>
                    </OutlinedWrapper>
                )}
            </div>
        );
    },
);

export default Step1;
